import { useEffect, useState } from 'react';
import {
  EXPENSE_CATEGORY_LABEL,
  type ExpenseCategory,
  type PeriodSummary,
  type Role,
} from 'shared';
import { api } from '../api';
import Icon from '../Icon';
import { haptic } from '../telegram';

const PERIODS = [
  { key: 'week', label: 'Неделя', days: 7 },
  { key: 'month', label: 'Месяц', days: 30 },
  { key: 'quarter', label: '3 месяца', days: 90 },
  { key: 'year', label: 'Год', days: 365 },
] as const;

type PeriodKey = (typeof PERIODS)[number]['key'];

/**
 * Цвета категорий трат.
 *
 * Закреплены за категорией навсегда: если топливо каждый раз меняет цвет,
 * диаграмму приходится читать заново, а не узнавать.
 */
const CATEGORY_COLOR: Record<ExpenseCategory, string> = {
  FUEL: '#f59e0b',
  TOLL: '#3b82f6',
  PARKING: '#8b5cf6',
  LOADING: '#10b981',
  REPAIR: '#ef4444',
  FINE: '#e11d48',
  MEAL: '#14b8a6',
  PACKING: '#a855f7',
  INSURANCE: '#0ea5e9',
  DEMURRAGE: '#f97316',
  OTHER: '#94a3b8',
};

const money = (v: number) => `${Math.round(v).toLocaleString('ru-RU')} ₽`;

/** Короткая запись для крупных сумм: 345 300 → 345,3 тыс. */
function short(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} млн ₽`;
  if (abs >= 100_000) return `${Math.round(v / 1000)} тыс. ₽`;
  return money(v);
}

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

/**
 * Кольцевая диаграмма трат.
 *
 * Кольцо, а не круг: в центре помещается итог, ради которого сюда и смотрят.
 * Сегменты рисуются обводкой окружности через dasharray — это дешевле,
 * чем считать дуги путями, и не даёт щелей между секторами.
 */
function Donut({
  items,
  total,
}: {
  items: { category: ExpenseCategory; amountRub: number }[];
  total: number;
}) {
  const R = 54;
  const C = 2 * Math.PI * R;
  let offset = 0;

  return (
    <div className="donut">
      <svg viewBox="0 0 140 140" role="img" aria-label="Траты по категориям">
        <circle
          cx="70"
          cy="70"
          r={R}
          fill="none"
          stroke="var(--surface-3)"
          strokeWidth="16"
        />
        {items.map((it) => {
          const len = total > 0 ? (it.amountRub / total) * C : 0;
          const dash = `${len} ${C - len}`;
          const el = (
            <circle
              key={it.category}
              cx="70"
              cy="70"
              r={R}
              fill="none"
              stroke={CATEGORY_COLOR[it.category]}
              strokeWidth="16"
              strokeDasharray={dash}
              strokeDashoffset={-offset}
              // Отсчёт с двенадцати часов, как на любом циферблате.
              transform="rotate(-90 70 70)"
            />
          );
          offset += len;
          return el;
        })}
      </svg>

      <div className="donut__center">
        <span className="meta">Траты</span>
        <strong>{short(total)}</strong>
      </div>
    </div>
  );
}

/**
 * Деньги перевозчика.
 *
 * Порядок как в банковском приложении: сначала одна цифра, ради которой
 * зашли, потом два потока — приход и расход, потом их разложение.
 * Водителю нужна не бухгалтерия, а ответ «сколько осталось после солярки».
 */
interface Props {
  /** Роль пользователя: у логиста своя книга расходов. */
  role: Role;
}

export default function Money({ role }: Props) {
  // Логист смотрит затраты на перевозки, перевозчик — заработок.
  // У экспедитора обе стороны, поэтому даём переключатель.
  const [side, setSide] = useState<'carrier' | 'shipper'>(
    role === 'LOGIST' ? 'shipper' : 'carrier',
  );
  const [period, setPeriod] = useState<PeriodKey>('month');
  const [data, setData] = useState<PeriodSummary | null>(null);
  const [prev, setPrev] = useState<PeriodSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const days = PERIODS.find((p) => p.key === period)!.days;
    const now = Date.now();
    const from = new Date(now - days * 864e5).toISOString();

    setLoading(true);
    Promise.all([
      api.summary({ from, as: side }),
      // Предыдущий такой же отрезок — только чтобы показать стрелку роста.
      api
        .summary({
          from: new Date(now - 2 * days * 864e5).toISOString(),
          to: from,
          as: side,
        })
        .catch(() => null),
    ])
      .then(([cur, before]) => {
        setData(cur);
        setPrev(before);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period, side]);

  const spentShare =
    data && data.payoutRub > 0
      ? Math.min(100, Math.round((data.expensesRub / data.payoutRub) * 100))
      : 0;

  const delta =
    data && prev && prev.netRub !== 0
      ? Math.round(((data.netRub - prev.netRub) / Math.abs(prev.netRub)) * 100)
      : null;

  return (
    <>
      <div className="page-head">
        <h2 style={{ margin: 0 }}>Финансы</h2>
      </div>

      {role === 'BOTH' && (
        <div className="chips" style={{ marginBottom: 12 }}>
          <button
            className={side === 'carrier' ? 'chip chip--on' : 'chip'}
            onClick={() => { haptic(); setSide('carrier'); }}
          >
            Как перевозчик
          </button>
          <button
            className={side === 'shipper' ? 'chip chip--on' : 'chip'}
            onClick={() => { haptic(); setSide('shipper'); }}
          >
            Как заказчик
          </button>
        </div>
      )}

      <div className="chips" style={{ marginBottom: 16 }}>
        {PERIODS.map((p) => (
          <button
            key={p.key}
            className={period === p.key ? 'chip chip--on' : 'chip'}
            onClick={() => { haptic(); setPeriod(p.key); }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading && <div className="card">Считаю…</div>}

      {!loading && data && data.ordersDone === 0 && (
        <div className="empty">
          За этот период завершённых рейсов нет.
          <br />
          Считаются только закрытые сделки.
        </div>
      )}

      {!loading && data && data.ordersDone > 0 && (
        <>
          {/* Главная цифра. Остальное на экране объясняет, как она вышла. */}
          <div className="card hero">
            <span className="meta">
              {side === 'shipper' ? 'Потрачено на перевозки' : 'Чистыми за период'}
            </span>
            <div
              className={
                side === 'carrier' && data.netRub < 0 ? 'hero__sum neg' : 'hero__sum'
              }
            >
              {money(side === 'shipper' ? Math.abs(data.netRub) : data.netRub)}
            </div>

            <div className="hero__row">
              {delta !== null && (
                <span className={delta >= 0 ? 'trend trend--up' : 'trend trend--down'}>
                  <Icon name={delta >= 0 ? 'up' : 'down'} size={14} />
                  {Math.abs(delta)}%
                </span>
              )}
              <span className="meta">
                {data.ordersDone}{' '}
                {plural(data.ordersDone, 'рейс', 'рейса', 'рейсов')}
                {data.distanceKm > 0
                  ? ` · ${data.distanceKm.toLocaleString('ru-RU')} км`
                  : ''}
                {data.netPerKm != null ? ` · ${data.netPerKm} ₽/км` : ''}
              </span>
            </div>
          </div>

          {/* Два потока рядом — приход и расход, как в банке. */}
          <div className="flow">
            <div className="flow__card">
              <span className="meta">
                {side === 'shipper' ? 'Ставки перевозчикам' : 'Заработано'}
              </span>
              <strong className={side === 'shipper' ? '' : 'pos'}>
                {money(Math.abs(data.payoutRub))}
              </strong>
              <div className="flow__bar">
                <span style={{ width: '100%', background: 'var(--success)' }} />
              </div>
            </div>

            <div className="flow__card">
              <span className="meta">
                {side === 'shipper' ? 'Сопутствующие расходы' : 'Потрачено'}
              </span>
              <strong>{money(data.expensesRub)}</strong>
              <div className="flow__bar">
                {/* Полоса показывает долю трат в доходе: пустая — рейс
                    отработан без затрат, полная — доход съеден целиком. */}
                <span
                  style={{
                    width: `${spentShare}%`,
                    background: spentShare > 70 ? 'var(--danger)' : 'var(--accent)',
                  }}
                />
              </div>
              <span className="meta">{spentShare}% от дохода</span>
            </div>
          </div>

          {data.byCategory.length > 0 ? (
            <div className="card">
              <h3>Куда ушли деньги</h3>

              <Donut items={data.byCategory} total={data.expensesRub} />

              <div className="cat-list">
                {data.byCategory.map((c) => {
                  const share =
                    data.expensesRub > 0
                      ? Math.round((c.amountRub / data.expensesRub) * 100)
                      : 0;
                  return (
                    <div key={c.category} className="cat">
                      <span
                        className="cat__dot"
                        style={{ background: CATEGORY_COLOR[c.category] }}
                        aria-hidden
                      />
                      <span className="cat__name">
                        {EXPENSE_CATEGORY_LABEL[c.category]}
                      </span>
                      <span className="cat__share meta">{share}%</span>
                      <span className="cat__sum tabular">{money(c.amountRub)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="card">
              <h3>Расходы не записаны</h3>
              <div className="meta">
                Отмечайте топливо и платные дороги внутри рейса — тогда здесь
                будет видно, сколько остаётся на руки на самом деле.
              </div>
            </div>
          )}

          <div className="card">
            <div className="kv">
              <span className="meta">Комиссия площадки</span>
              <span className="tabular">{money(data.feeRub)}</span>
            </div>
            <div className="kv">
              <span className="meta">В среднем за рейс</span>
              <span className="tabular">{money(data.netRub / data.ordersDone)}</span>
            </div>
            {data.distanceKm > 0 && (
              <div className="kv">
                <span className="meta">Расходы на километр</span>
                <span className="tabular">
                  {Math.round(data.expensesRub / data.distanceKm)} ₽
                </span>
              </div>
            )}
          </div>

          <div className="meta" style={{ padding: '0 4px 8px' }}>
            Считаются завершённые рейсы: пока перевозка не закрыта, доход
            не учитывается.
          </div>
        </>
      )}
    </>
  );
}
